/* eslint-disable jsx-a11y/alt-text */
import { Card, Flex } from "@radix-ui/themes";
import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import Draggable from "react-draggable";
import { Button, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@radix-ui/themes';
import * as Select from '@radix-ui/react-select';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, Cross1Icon } from '@radix-ui/react-icons';
import iso_region_map from '../../json/iso_to_region_name.json'

/**
 * SelectItem component for rendering an item in the Radix Select dropdown.
 * 
 * @param {object} props - Props passed to the component.
 * @param {string} props.children - The text to display for the item.
 * @param {string} props.className - Additional classNames for styling.
 * @param {function} props.forwardedRef - Reference for the DOM element.
 */
const SelectItem = React.forwardRef(({ children, className, ...props }, forwardedRef) => {
    return (
        <Select.Item className={classnames('SelectItem', className)} {...props} ref={forwardedRef}>
            <Select.ItemText>{children}</Select.ItemText>
            <Select.ItemIndicator className="SelectItemIndicator">
                <CheckIcon />
            </Select.ItemIndicator>
        </Select.Item>
    );
});

/**
 * Leaderboard component that displays a table of sorted data based on a selected metric (Download or Upload Speed).
 * 
 * @param {object} props - Props passed to the component.
 * @param {function} props.hide - Function to hide/close the leaderboard.
 * @param {array} props.data - Array of data to display in the leaderboard.
 * @param {string} props.Type - The type of leaderboard (e.g., "REGION" or "ISP").
 */
function Leaderboard({ hide, data, Type }) {
    // State for selected metric (Download Speed or Upload Speed).
    const [selectedValue, setSelectedValue] = useState("Download Speed");

    // State to hold the sorted leaderboard data.
    const [lb_data, setLBData] = useState([]);

    /**
     * useEffect hook to sort the data based on the selected metric whenever the metric or data changes.
     */
    useEffect(() => {
        // Deep copy the data to avoid mutating the original array.
        const deepCopy = JSON.parse(JSON.stringify(data));

        if (selectedValue === "Download Speed") {
            // Sort by download speed in descending order.
            deepCopy.sort((a, b) => b.download_speed - a.download_speed);
            setLBData(deepCopy);
        } else if (selectedValue === "Upload Speed") {
            // Sort by upload speed in descending order.
            deepCopy.sort((a, b) => b.upload_speed - a.upload_speed);
            setLBData(deepCopy);
        } else {
            console.error("Invalid selection");
        }
    }, [selectedValue, data]);

    /**
     * Function to handle when a different metric is selected from the dropdown.
     * 
     * @param {string} metric - The selected metric ("Download Speed" or "Upload Speed").
     */
    function onSelectChange(metric) {
        setSelectedValue(metric);
    }
    return (
        <Flex
            style={{
                position: 'fixed',
                top: '20%',
                left: '50%',
                transform: 'translate(-50%, 0)',
                flexDirection: 'column',
                height: '50vh',
                width: '25vw', // Use viewport width for better responsiveness
                padding: '20px', // Adjust padding for better spacing
                border: '1px solid #ccc',
                overflowY: 'auto',
                borderRadius: '8px', // Add rounded corners for a softer look
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', // Add subtle shadow for depth
                backgroundColor: '#000', // Ensure background color contrasts with content
            }}
        >
            <Flex>
                <Select.Root onValueChange={onSelectChange} defaultValue='Download Speed'>
                    <Select.Trigger style={{ backgroundColor: 'black' }} className="SelectTrigger" aria-label="Metrics">
                        <Select.Value placeholder="Change Map Metrics" />
                        <Select.Icon className="SelectIcon">
                            <ChevronDownIcon />
                        </Select.Icon>
                    </Select.Trigger>
                    <Select.Portal>
                        <Select.Content className="SelectContent">
                            <Select.ScrollUpButton className="SelectScrollButton">
                                <ChevronUpIcon />
                            </Select.ScrollUpButton>
                            <Select.Viewport className="SelectViewport">
                                <Select.Group>
                                    <SelectItem value="Download Speed">Download Speed</SelectItem>
                                    <SelectItem value="Upload Speed">Upload Speed</SelectItem>
                                </Select.Group>
                            </Select.Viewport>
                            <Select.ScrollDownButton className="SelectScrollButton">
                                <ChevronDownIcon />
                            </Select.ScrollDownButton>
                        </Select.Content>
                    </Select.Portal>
                </Select.Root>
                <Button style={{ position: 'absolute', left: '90%' }} variant="soft" onClick={hide} color='purple' size={'1'}><Cross1Icon /></Button>
            </Flex>
            <Table.Root>
                <Table.Header>
                    <Table.Row>
                        <Table.ColumnHeaderCell>{Type} Name</Table.ColumnHeaderCell>
                        <Table.ColumnHeaderCell>{selectedValue}</Table.ColumnHeaderCell>
                    </Table.Row>
                </Table.Header>

                <Table.Body style={{ height: 'fit-content' }} >
                    {lb_data.map((entry, index) => (
                        (Type !== "REGION" || entry.iso in iso_region_map) && (Type !== "ISP" || entry.isp_name) ?
                            <Table.Row key={index}>
                                <Table.RowHeaderCell>
                                    <Flex style={{ width: '100%' }}>
                                        {`${index + 1}) `}
                                        {(Type === "ISP" ? entry.isp_name : iso_region_map[entry.iso])}
                                        {index === 0 && <img src="imgs/gold.png" style={{
                                            maxHeight: '35px',
                                            marginRight: '10px'
                                        }} />}
                                    </Flex>
                                </Table.RowHeaderCell>
                                <Table.Cell> {selectedValue === "Download Speed" ? entry.download_speed.toFixed(2) : entry.upload_speed.toFixed(2)} mbps </Table.Cell>
                            </Table.Row> : ''
                    ))}
                </Table.Body>
            </Table.Root>
        </Flex>
    )
}

export default Leaderboard;
